import * as React from "react";
import Modal from "@mui/material/Modal";
import { Box, styled } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import theme from "../../../theme";
import CrossIcon from "../../../assets/Icons/CrossIcon";
import CustomInput from "../../common/input/CustomInput";
import CustomDropdown from "../../common/common-dropdown/CustomDropdown";
import CustomButton from "../../common/button/CustomButton";
import { StyledBoxFlex } from "../success-virtual-locker/style";
import { StyledBoxCard } from "../setting-coupon/style";
import { StyledTypography } from "../manual-split-calculation/style";
import { SHOPPER_ADD, SHOPPER_ROLE_LIST } from "../../../actions/admin/customer-actions";
import { formatName } from "../../../utils/common";

const StyledModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  background: theme.palette.custom.white,
  borderRadius: theme.borderRadius.radius.large,
  outline: "none",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: { width: 500 },
  [theme.breakpoints.down("sm")]: { width: 350 },
  [theme.breakpoints.down("xs")]: { width: 270 },
}));

const ScrollableContent = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  padding: "20px",
}));

const FooterWrapper = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "16px 20px",
  borderTop: "1px solid #E5E7EB",
  background: "#fff",
  flexShrink: 0,
}));

const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  userRole: "",
  password: "",
  confirmPassword: "",
};

const validationSchema = Yup.object({
  first_name: Yup.string()
    .trim()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),
  last_name: Yup.string()
    .trim()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters"),
  email: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Email is required"),
  userRole: Yup.string().required("User role is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

export default function CreateShopper({ open, handleClose, shopperDetails }) {
  const dispatch = useDispatch();
  const submitLock = React.useRef(false);
  const { isLoading, shopperRoles } = useSelector((state) => state?.shopper);

  React.useEffect(() => {
    if (!open) {
      submitLock.current = false;
      return;
    }
    dispatch({ type: SHOPPER_ROLE_LIST });
  }, [dispatch, open]);

  const roleOptions = React.useMemo(
    () =>
      (shopperRoles || []).map((role) => ({
        label: formatName(
          role?.name || role?.role_name || role?.title || role?.id || ""
        ),
        value: role?.id,
      })),
    [shopperRoles]
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <StyledModalBox>
        {/* Sticky Header */}
        <StyledBoxCard>
          <StyledBoxFlex>
            <StyledTypography>Add Shopper</StyledTypography>
          </StyledBoxFlex>
          <CrossIcon color={theme.palette.custom.white} onClick={handleClose} style={{ cursor: "pointer" }} />
        </StyledBoxCard>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => {
            if (submitLock.current || isLoading) return;
            submitLock.current = true;

            dispatch({
              type: SHOPPER_ADD,
              payload: {
                ...values,
                onSuccess: () => {
                  shopperDetails();
                  resetForm();
                  handleClose();
                },
                onError: () => {
                  submitLock.current = false;
                },
              },
            });
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
            <Form style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
              {/* Scrollable Content */}
              <ScrollableContent>
                <Grid container spacing={2}>
                  <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                    <CustomInput
                      tableField
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      customLabel="First Name"
                      placeholder="Enter first name"
                      name="first_name"
                      value={values.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.first_name && Boolean(errors.first_name)}
                      helperText={touched.first_name && errors.first_name}
                    />
                  </Grid>

                  <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                    <CustomInput
                      tableField
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      customLabel="Last Name"
                      placeholder="Enter last name"
                      name="last_name"
                      value={values.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.last_name && Boolean(errors.last_name)}
                      helperText={touched.last_name && errors.last_name}
                    />
                  </Grid>

                  <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                    <CustomInput
                      tableField
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      customLabel="Email"
                      placeholder="Enter email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>

                  <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                    <CustomDropdown
                      tableField
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      customLabel="User Role"
                      placeholder="Select User Role"
                      name="userRole"
                      value={values.userRole}
                      onBlur={handleBlur}
                      options={roleOptions}
                      setFieldValue={setFieldValue}
                      error={touched.userRole && Boolean(errors.userRole)}
                      helperText={touched.userRole && errors.userRole}
                    />
                  </Grid>

                  <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                    <CustomInput
                      tableField
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      customLabel="Password"
                      placeholder="Enter password"
                      type="password"
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                    />
                  </Grid>

                  <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                    <CustomInput
                      tableField
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      customLabel="Confirm Password"
                      placeholder="Enter confirm password"
                      type="password"
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                    />
                  </Grid>
                </Grid>
              </ScrollableContent>

              {/* Sticky Footer */}
              <FooterWrapper>
                <CustomButton
                  padding="12px 40px"
                  btntype="white"
                  color={theme.palette.custom.virtualTitle}
                  border="1px solid #D0D5DD"
                  btnLabel="Cancel"
                  onClick={handleClose}
                />
                <CustomButton
                  padding="12px 40px"
                  type="submit"
                  btntype="primary"
                  color={theme.palette.custom.white}
                  btnLabel="Create Shopper"
                  disabled={isLoading}
                  isLoading={isLoading}
                />
              </FooterWrapper>
            </Form>
          )}
        </Formik>
      </StyledModalBox>
    </Modal>
  );
}
